import { useCallback, useEffect, useState } from "react";

export interface Consulta<T> {
  dados: T | undefined;
  erro: Error | undefined;
  recarregar: () => void;
}

/**
 * Lê um recurso da API e, se `intervalo` devolver milissegundos, consulta de novo depois disso
 * (o "checando" do card). Troca de `chave` refaz a leitura; respostas velhas são descartadas.
 */
export function useConsulta<T>(
  chave: string,
  ler: () => Promise<T>,
  intervalo?: (dados: T) => number | false,
): Consulta<T> {
  const [estado, setEstado] = useState<{ chave: string; dados?: T; erro?: Error }>({ chave });
  const [rodada, setRodada] = useState(0);
  const recarregar = useCallback(() => {
    setRodada((n) => n + 1);
  }, []);

  useEffect(() => {
    let vivo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    const buscar = () => {
      ler().then(
        (dados) => {
          if (!vivo) return;
          setEstado({ chave, dados });
          const ms = intervalo?.(dados);
          if (ms) espera = setTimeout(buscar, ms);
        },
        (erro: unknown) => {
          if (vivo)
            setEstado({ chave, erro: erro instanceof Error ? erro : new Error(String(erro)) });
        },
      );
    };
    buscar();
    return () => {
      vivo = false;
      clearTimeout(espera);
    };
    // `ler` e `intervalo` mudam a cada render; a leitura depende só da chave e da rodada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, rodada]);

  const atual = estado.chave === chave;
  return {
    dados: atual ? estado.dados : undefined,
    erro: atual ? estado.erro : undefined,
    recarregar,
  };
}
